'use client';

import { TypedLocale } from 'payload';
import React from 'react';

import { ImageMedia } from '@/components/ImageMedia';
import RichText from '@/components/RichText';
import type {
  Employee,
  EmployeeBlock as EmployeeBlockProps,
} from '@/payload-types';
import { cn } from '@/utils/cn';
import { getServerTranslations } from '@/utils/getDictionary';
import { pushGTMEvent } from '@/utils/pushGTMEvent';

import { BlockSettings } from '../BlockSettings';
import styles from './block.module.scss';

type Props = EmployeeBlockProps & {
  locale: TypedLocale;
};

export const EmployeeBlock: React.FC<Props> = (props) => {
  const {
    backgroundColor,
    individualSpacing,
    locale,
    richText,
    selectedEmployee,
    spacing,
    spacingBottom,
    spacingTop,
  } = props;

  const { email, image, linkedin, name, phone, role } =
    selectedEmployee as Employee;

  const t = getServerTranslations(locale);

  const handleClick =
    (meta: { contact_form: string; contact_name?: string }) => () => {
      pushGTMEvent({
        event: 'contact_click',
        ...meta,
      });
    };

  if (!selectedEmployee) return null;

  return (
    <BlockSettings
      backgroundColor={backgroundColor}
      individualSpacing={individualSpacing}
      spacing={spacing}
      spacingBottom={spacingBottom}
      spacingTop={spacingTop}
    >
      <div className={cn('container', styles.block)}>
        {image && (
          <ImageMedia
            image={image}
            aspectRatio={'employee'}
            className={styles.image}
            responsiveWidths={{
              mobile: 343,
              desktop: 480,
              largeDesktop: 720,
            }}
          />
        )}
        <div className={styles.info}>
          {richText && <RichText content={richText} enableGutter={false} />}
          <div className={styles.about}>
            <span className={styles.bold}>{t('employee.contact')}</span>
            <span className='h5'>{name}</span>
            <p>{role}</p>
          </div>
          <div className={styles.links}>
            {email && (
              <a
                onClick={handleClick({
                  contact_form: 'email',
                  contact_name: name,
                })}
                href={`mailto:${email}`}
              >
                {email}
              </a>
            )}
            {phone && (
              <a
                onClick={handleClick({
                  contact_form: 'phone',
                  contact_name: name,
                })}
                href={`tel:${phone}`}
              >
                {phone}
              </a>
            )}
            {linkedin && (
              <a
                className={styles.linkedin}
                onClick={handleClick({
                  contact_form: 'linkedin',
                  contact_name: name,
                })}
                href={linkedin ?? ''}
                rel='noreferrer'
                target='_blank'
              >
                <svg
                  width='32'
                  height='33'
                  viewBox='0 0 32 33'
                  fill='none'
                  xmlns='http://www.w3.org/2000/svg'
                >
                  <path
                    d='M0 16.0273C0 7.19079 7.16344 0.0273438 16 0.0273438C24.8366 0.0273438 32 7.19079 32 16.0273C32 24.8639 24.8366 32.0273 16 32.0273C7.16344 32.0273 0 24.8639 0 16.0273Z'
                    fill='#F3F2FF'
                  />
                  <path
                    d='M11.6 24.0273H8.2V13.3273H11.6V24.0273ZM9.9 11.8273C8.8 11.8273 8 11.0273 8 9.92734C8 8.82734 8.9 8.02734 9.9 8.02734C11 8.02734 11.8 8.82734 11.8 9.92734C11.8 11.0273 11 11.8273 9.9 11.8273ZM24 24.0273H20.6V18.2273C20.6 16.5273 19.9 16.0273 18.9 16.0273C17.9 16.0273 16.9 16.8273 16.9 18.3273V24.0273H13.5V13.3273H16.7V14.8273C17 14.1273 18.2 13.0273 19.9 13.0273C21.8 13.0273 23.8 14.1273 23.8 17.4273V24.0273H24Z'
                    fill='#2A0D5D'
                  />
                </svg>
                {t('employee.connectOnLinkedIn')}
              </a>
            )}
          </div>
        </div>
      </div>
    </BlockSettings>
  );
};
